import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ApplicationAccessFooter from "../ApplicationAccessFooter/ApplicationAccessFooter";
import { AppDispatch } from "../../store/store";
import { triggerToast } from "../../utils/toast";
import {
  updateCurrentStep,
  clearFeatureSelection,
  updateMultipleFeatureSelection,
} from "../../store/slices/featureGroupWithFeatureSlice";
import { getEnv } from "../../Constants/env";
import {
  extractUniqueFeatureIds,
  setFeaturePermissionState,
  submitFeaturePermissionData,
} from "../../store/slices/featurePermissionSlice";
import axios from "axios";
import FeaturePermissionsNewTable from "./FeaturePermissionsNewTable";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Button from "../../Components/Common/CprimeButton";
import IneligibleSelections from "./IneligibleSelections";
import { useIdToken } from "../../hooks/useAuthSession";

type User = {
  displayName: string;
  mail: string;
  userPrincipalName?: string;
  user_workday_id: string;
  action?: string;
};

export default function FeaturePermissionsNew({
  selectedUserList = [],
  onBack,
}: {
  selectedUserList?: User[];
  onBack?: any;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const idToken = useIdToken();
  const [uniqueFeatureIds, setUniqueFeatureIds] = useState<string[]>([]);
  const [featureTableRows, setFeatureTableRows] = useState<any>({});
  const [submitButtonState, setSubmitButtonState] = useState<boolean>(true);
  const [deletedFeatures, setDeletedFeatures] = useState<
    { groupId: string; featureId: string }[]
  >([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [ineligibleData, setIneligibleData] = useState<any>(null);
  const justificationInfoRef = useRef<{ [key: string]: string }>({});
  const { env } = useSelector((state: any) => state?.createApplication);

  const featureGroups = useSelector(
    (state: any) => state.featureGroupWithFeature.data,
  );

  const featurePermission = useSelector(
    (state: any) => state.featurePermission.featurePermissionState,
  );
  const clonedFeatureIds = useSelector(
    (state: any) => state.featurePermission?.clonedFeatureIds,
  );

  const onJustficationChange = (value: string, featureId: any) => {
    justificationInfoRef.current[featureId] = value;
    const allRowsHaveJustification = featureTableRows?.rows?.every(
      (row: any) => {
        return (
          justificationInfoRef.current[row.feature_id] &&
          justificationInfoRef.current[row.feature_id].trim() !== "" &&
          row?.value?.selected?.length > 0
        );
      },
    );
    setSubmitButtonState(!allRowsHaveJustification);
  };

  const handleSubmit = async () => {
    try {
      setSubmitButtonState(true);
      const mustSelectViewPerm = featureTableRows?.rows?.every((row: any) =>
        row?.value?.selected?.some((item: any) => item.value === "View"),
      );

      const transformedData = featureTableRows?.rows?.flatMap((row: any) =>
        row?.value?.selected
          ?.filter((rowItem: { disabled: boolean }) => !rowItem?.disabled)
          .map((item: any) => ({
            permissionId: item?.id,
            featureId: row?.feature_id,
            justification: row?.inputtext,
          })),
      );
      if (transformedData.length === 0) {
        triggerToast("No permission selected", "error");
        return;
      }

      const response = await dispatch(
        submitFeaturePermissionData({
          selectedPermissions: transformedData,
          userData: (selectedUserList || []).map(({ action, ...rest }) => rest),
          validateBeforeSubmit: true,
        }),
      );
      if (response?.payload?.error && response?.payload?.message) {
        setErrorMessage(response?.payload?.message);
        setIneligibleData(response?.payload?.data);
        setOpenDialog(true);
      } else if (response?.payload?.status?.toLowerCase() === "success") {
        dispatch(clearFeatureSelection());
        dispatch(setFeaturePermissionState([]));
        dispatch(updateCurrentStep(2));
        triggerToast("Request submitted successfully", "success");
        navigate("/request-status");
      }
    } catch (error) {
    } finally {
      setSubmitButtonState(false);
    }
  };

  const handleCloseDialog = () => setOpenDialog(false);
  const handleSubmitRequest = async () => {
    const response = await dispatch(
      submitFeaturePermissionData({
        selectedPermissions: featureTableRows?.rows?.flatMap((row: any) =>
          row?.value?.selected
            ?.filter((rowItem: { disabled: boolean }) => !rowItem?.disabled)
            .map((item: any) => ({
              permissionId: item?.id,
              featureId: row?.feature_id,
              justification: row?.inputtext,
            })),
        ),
        userData: (selectedUserList || []).map(({ action, ...rest }) => rest),
        validateBeforeSubmit: false,
      }),
    );

    if (response?.payload?.error && response?.payload?.message) {
      setErrorMessage(response.payload.message);
      setIneligibleData(response?.payload?.data);
      setOpenDialog(true);
    } else if (response?.payload?.status?.toLowerCase() === "success") {
      dispatch(clearFeatureSelection());
      dispatch(setFeaturePermissionState([]));
      dispatch(updateCurrentStep(2));
      triggerToast("Request submitted successfully", "success");
      setOpenDialog(false);
      navigate("/request-status");
    }
  };

  useEffect(() => {
    const extractedFeatureIds = extractUniqueFeatureIds(featureGroups);
    setUniqueFeatureIds(extractedFeatureIds as string[]);
  }, [featureGroups]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      if (uniqueFeatureIds?.length > 0 || clonedFeatureIds?.length > 0) {
        const extractedFeatureIds =
          clonedFeatureIds?.length > 0
            ? clonedFeatureIds
            : extractUniqueFeatureIds(featureGroups);
        const response = await axios.post(
          `${getEnv()?.baseURL}/permissiondetails?feature_id=${extractedFeatureIds.join(",")}&env=${env}`,
          {
            sortingFilter: {
              filter: [],
              sort: [],
              page: {
                limit: 100,
                offset: 0,
              },
            },
            userData: selectedUserList?.map((item: any) => ({
              displayName: item?.displayName,
              mail: item?.mail,
              user_workday_id: item?.user_workday_id,
            })),
          },
          {
            headers: {
              Authorization: idToken,
              "x-api-key": "sasa",
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          },
        );

        if (featurePermission?.length > 0) {
          featurePermission.forEach((update: any) => {
            const feature = response?.data?.data?.rows?.find(
              (item: any) => item.feature_id === update.featureId,
            );

            if (feature) {
              feature.inputtext = update.justification;
              feature.value.selected = feature?.value?.default?.filter(
                (defaultItem: any) =>
                  update?.permission?.includes(defaultItem?.id),
              );
            }
          });
        }

        // Update error property
        await Promise.all(
          response?.data?.data?.rows?.map(async (row: any) => {
            const hasView = row?.value?.selected?.some(
              (item: any) => item?.value?.toLowerCase() === "view",
            );
            //row.value.error = !hasView; // Set error based on hasView
          }),
        );
        // Find the column with name "Justification" and change its type
        const columns = response?.data?.data?.columns;
        for (let col of columns) {
          if (col.name === "Justification" && col.type === "INPUTTEXT") {
            col.type = "COMPONENT";
            col.id = "COMPONENT";
            break;
          }
        }
        setFeatureTableRows(response.data.data);
      }
    } catch (err) {
      // console.error("Error fetching data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (clonedFeatureIds?.rows?.length > 0) {
      let newFeatureIds = { ...clonedFeatureIds };
      let columns = newFeatureIds.columns.map((col: any) => {
        if (col.name === "Justification" && col.type === "INPUTTEXT") {
          return { ...col, type: "COMPONENT", id: "COMPONENT" };
        }
        return col;
      });
      newFeatureIds.columns = columns;
      setFeatureTableRows(newFeatureIds);
      setIsLoading(false);
    } else {
      fetchData();
    }
  }, [uniqueFeatureIds, clonedFeatureIds]);

  useEffect(() => {
    const SelectedFeaturesState = featureTableRows?.rows?.map((row: any) => ({
      featureId: Number(row.feature_id),
      permission: row?.value?.selected
        ?.filter((perm: any) => !perm.disabled)
        .map((perm: any) => perm.id),
      justification: row.inputtext,
    }));

    setSubmitButtonState(
      SelectedFeaturesState && SelectedFeaturesState?.length > 0
        ? SelectedFeaturesState.some(
            (item: any) =>
              item?.permission?.length === 0 || item?.justification === "",
          )
        : true,
    );

    if (SelectedFeaturesState && SelectedFeaturesState.length > 0) {
      dispatch(setFeaturePermissionState(SelectedFeaturesState));
    }

    // const mustSelectViewPerm = featureTableRows?.rows?.every((row: any) =>
    //   row.value.selected.some((item: any) => item.value === "View"),
    // );
  }, [featureTableRows]);

  const handleDeleteRow = (event: any, data: any) => {
    const featureId = data?.row?.original?.feature_id;
    delete justificationInfoRef.current[featureId];

    setFeatureTableRows((prevData: any) => ({
      ...prevData,
      totalCount: (prevData?.rows?.length - 1).toString(),
      rows: prevData?.rows?.filter((row: any) => row.feature_id !== featureId),
    }));

    const groupId = featureGroups?.find((group: any) =>
      group?.features?.some((feature: any) => feature?.id === featureId),
    )?.groupId;
    setDeletedFeatures((prev) => [...(prev || []), { groupId, featureId }]);
  };

  const goBackToStep1 = () => {
    dispatch(updateMultipleFeatureSelection(deletedFeatures));
    dispatch(updateCurrentStep(2));
  };

  return (
    <>
      <FeaturePermissionsNewTable
        setFeatureTableRows={setFeatureTableRows}
        isLoading={isLoading}
        featureTableRows={featureTableRows}
        handleDeleteRow={handleDeleteRow}
        onJustficationChange={onJustficationChange}
      />
      <ApplicationAccessFooter
        isBack
        isClear={false}
        isNext={false}
        onBack={goBackToStep1}
        isSubmitDisabled={submitButtonState}
        onSubmit={handleSubmit}
      />
      {errorMessage && (
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md">
          <DialogTitle>
            Ineligible Selection(s)
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseDialog}
              aria-label="close"
              style={{ position: "absolute", right: 24, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent style={{ padding: "0 24px" }}>
            <IneligibleSelections errorData={ineligibleData} />
          </DialogContent>
          <DialogActions
            style={{
              borderTop: "1px solid #e9ecee",
              padding: "16px 24px 24px 24px",
            }}
          >
            <Button
              onClick={handleCloseDialog}
              style={{
                padding: "12px 16px",
                border: "1px solid #000",
                borderRadius: "4px",
                color: "#000",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitRequest}
              style={{
                padding: "12px 16px",
                border:
                  ineligibleData?.validCount <= 0
                    ? "1px solid #a4a5a5"
                    : "1px solid #2469FF",
                borderRadius: "4px",
                color:
                  ineligibleData?.validCount <= 0
                    ? "rgb(39 38 38 / 70%)"
                    : "white",
                background:
                  ineligibleData?.validCount <= 0
                    ? "rgb(165 164 164 / 49%)"
                    : "#2469FF",
              }}
              disabled={ineligibleData?.validCount <= 0}
            >
              {`Proceed with ${ineligibleData?.validCount} Selection(s)`}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
